import React, { useCallback, useEffect, useState } from 'react';
import { Card, Pagination, TableData } from '@/components';
import { Column } from '@/types/common';
import { ApiPagination } from '@/types/package';

export interface QueuePage<T> {
  items: T[];
  pagination: ApiPagination;
}

interface ActionQueueSectionProps<T> {
  /** Optional — omit when an outer tab already names the queue. */
  title?: string;
  columns: Column<T>[];
  /** One server call per queue — each owns its own page and page size. */
  fetchPage: (page: number, size: number) => Promise<QueuePage<T>>;
  emptyText: string;
  onRowClick?: (record: T) => void;
}

/**
 * One dashboard queue: a titled card with its own server-paged table.
 *
 * Loading and errors are handled here, per queue, so a failing endpoint blanks
 * only its own section while the other four keep rendering.
 */
const ActionQueueSection = <T extends object>({
  title,
  columns,
  fetchPage,
  emptyText,
  onRowClick,
}: ActionQueueSectionProps<T>) => {
  const [rows, setRows] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchPage(page, size);
      setRows(response?.items ?? []);
      setTotal(response?.pagination?.total ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal memuat data');
      setRows([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [fetchPage, page, size]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Card className="mb-4">
      {title && (
        <h2 className="text-[16px] font-semibold text-gray-900 mb-3">
          {title}
        </h2>
      )}

      {error && !isLoading ? (
        <div className="py-8 text-center">
          <p className="text-[14px] text-gray-600 mb-3">{error}</p>
          <button
            type="button"
            onClick={load}
            className="text-[13px] font-medium text-primary hover:underline"
          >
            Coba lagi
          </button>
        </div>
      ) : (
        <TableData
          data={rows}
          columns={columns}
          size="small"
          className="min-w-full"
          loading={isLoading}
          showHeaderWhenEmpty
          onRowClick={onRowClick}
          emptyState={
            <p className="py-10 text-center text-[14px] text-gray-500">
              {emptyText}
            </p>
          }
        />
      )}

      <Pagination
        currentPage={page}
        totalItems={total}
        itemsPerPage={size}
        onPageChange={setPage}
        onPageSizeChange={(newSize) => {
          setSize(newSize);
          setPage(1);
        }}
      />
    </Card>
  );
};

export default ActionQueueSection;
